import {
  getOrderPickupStops,
  formatOrderAddress,
} from '../helpers/order-delivery.helpers';
import { Cell } from 'react-table';
import {
  AdminRole,
  CustomerTierKeys,
  IOrder,
  IUser,
  KitchenRole,
  OrdersStatuses,
  RiderStatus,
} from '../types';
import numeral from 'numeral';
import { VehicleIcon } from '../helpers/riders.helpers';
import {
  CalendarDaysIcon,
  ClockIcon,
  CubeIcon,
  QuestionMarkCircleIcon,
  ShoppingBagIcon,
  TruckIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

const paymentMethods = [
  { value: 'TRANSFER', option: 'Transfer' },
  { value: 'WALLET', option: 'Wallet' },
  { value: 'CARD', option: 'Card' },
] as { value: string; option: string }[];

const orderStatus = [
  { value: 'PLACED', label: 'Placed', hideFor: ['vendor'] },
  { value: 'CONFIRMED', label: 'Confirmed', hideFor: ['vendor'] },
  { value: 'PREPARING', label: 'Preparing', hideFor: ['vendor'] },
  // { value: 'PACKED', label: 'Packed', hideFor: [] },
  { value: 'ACCEPTED', label: 'Accepted', hideFor: [] },
  { value: 'READY_FOR_PICKUP', label: 'Ready for Pickup', hideFor: [] },
  { value: 'ENROUTE', label: 'Enroute', hideFor: [] },
  { value: 'ARRIVED', label: 'Arrived', hideFor: [] },
  { value: 'DELIVERED', label: 'Delivered', hideFor: ['LOGISTICS'] },
  { value: 'CANCELED', label: 'Canceled', hideFor: [] },
] as { value: string; label: string; hideFor: (AdminRole | 'vendor')[] }[];

export const riderStatus = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'ENROUTE', label: 'Enroute' },
  { value: 'ARRIVED', label: 'Arrived' },
  { value: 'DELIVERED', label: 'Delivered', hideFor: ['LOGISTICS'] },
] as { value: RiderStatus; label: string; hideFor?: AdminRole[] }[];

const statusColors = {
  PREPARING: 'bg-preparing',
  ENROUTE: 'bg-enroute',
  DELIVERED: 'bg-delivered',
  CANCELED: 'bg-canceled',
  SCHEDULED: 'bg-chow-blue',
  PLACED: 'bg-placed',
  ACCEPTED: 'bg-accepted',
  successful: 'bg-enroute',
  ACTIVE: 'bg-enroute',
  INACTIVE: 'bg-canceled',
  Paid: 'bg-enroute',
  Pending: 'bg-preparing',
  OUT_OF_STOCK: 'bg-preparing',
  Enabled: 'bg-enroute',
  Disabled: 'bg-canceled',
};

const orderGroups = [
  { name: 'All Orders', value: '' },
  { name: 'New Orders', value: 'CONFIRMED' },
  // { name: 'Confirmed', value: 'CONFIRMED' },
  // { name: 'Preparing', value: 'PREPARING' },
  // { name: 'Accepted', value: 'ACCEPTED' },
  { name: 'Ready for Pickup', value: 'READY_FOR_PICKUP' },
  { name: 'Enroute', value: 'ENROUTE' },
  { name: 'Arrived', value: 'ARRIVED' },
  // { name: 'Delivered Orders', value: 'DELIVERED' },
  { name: 'Cancelled Orders', value: 'CANCELED' },
  { name: 'Scheduled Orders', value: 'SCHEDULED' },
  { name: 'Follow Up', value: 'FLAGGED' },
  { name: 'Other Channels', value: 'OTHER_CHANNELS' },
];

const paymentStatus = [
  { name: 'Pending', value: 0 },
  { name: 'Confirmed', value: 1 },
];

const orderColumns = (type: 'abandoned' | 'paid' = 'paid') => [
  {
    Header: 'Order Slug',
    accessor: 'slug',
    Cell: ({ row }: Cell) => {
      const order = row.original as IOrder;
      const segment = order.userSegment;

      const getTierStyles = (tier: CustomerTierKeys | undefined) => {
        switch (tier?.toUpperCase()) {
          case 'BRONZE':
            return 'text-[#CD7F32] bg-orange-50 border-orange-200';
          case 'GOLD':
            return 'text-[#FFC107] bg-amber-50 border-amber-200';
          case 'PLATINUM':
            return 'text-[#607D8B] bg-slate-50 border-slate-200 px-[1px]';
          case 'SILVER':
            return 'text-[#C0C0C0] bg-gray-50 border-gray-200';
          case 'ELITE':
            return 'text-[#00897B] bg-teal-50 border-teal-200';
          case 'CROWN':
            return 'text-[#B8860B] bg-yellow-50 border-yellow-200';
          case 'DIAMOND':
            return 'text-[#9999CC] bg-[#FAFAFA] border-[#E0E0E8] px-[1px]';
          default:
            return 'text-amber-500 bg-amber-100 border-amber-300';
        }
      };

      const segmentDisplay = {
        tier:
          segment?.tier?.toUpperCase() === 'CROWN'
            ? `${segment.tier} customer`
            : null,
        category:
          order.slug?.startsWith('EXT_') &&
          segment?.category?.trim().toLowerCase() === 'new customer'
            ? null
            : segment?.category || null,
        experience:
          segment?.experience?.toLowerCase() === 'bad experience'
            ? `${segment.experience}`
            : null,
      };
      return (
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium !font-mono whitespace-nowrap">
            {order.slug}
          </span>
          {order.slug?.startsWith('EXT_') && order.metadata?.orderSlug && (
            <span className="text-xs text-neutral-500 !font-mono">
              {order.metadata.orderSlug}
            </span>
          )}
          <div className="flex flex-col gap-1">
            {segmentDisplay.tier && (
              <div
                className={`text-[10px] text-center rounded-md border ${getTierStyles(
                  segment?.tier
                )}`}
              >
                {segmentDisplay.tier}
              </div>
            )}
            {segmentDisplay.category && (
              <div className="self-start whitespace-nowrap px-1.5 py-0.5 text-[10px] leading-none text-sky-700 bg-sky-50 rounded">
                {segmentDisplay.category}
              </div>
            )}
            {segmentDisplay.experience && (
              <div className="text-[10px] text-purple-600 bg-purple-100 border border-purple-300 text-center rounded-md">
                {segmentDisplay.experience}
              </div>
            )}
          </div>
        </div>
      );
    },
  },

  {
    Header: 'Fulfilment time',
    accessor: 'fulfilmentTime',
    disableSortBy: true,
  },

  ...(type === 'paid'
    ? [
        {
          Header: 'Date & Time',
          accessor: 'timePaid',
        },
      ]
    : [
        {
          Header: 'Date & Time',
          accessor: 'timePlaced',
        },
      ]),

  {
    Header: 'Customer',
    accessor: 'customer',
    Cell: ({ row }: Cell) =>
      row.values.customer
        ? row.values.customer.fullName
        : row.values.user
        ? `${row.values.user.firstName} ${row.values.user.lastName}`
        : '-',
  },
  {
    Header: 'Distance from Kitchen (KM)',
    accessor: 'estimatedDistanceTravelled',
    Cell: ({ row }: Cell) =>
      `${Math.round(row.values.estimatedDistanceTravelled / 1000)}`,
  },
  {
    Header: 'Pilot',
    accessor: 'pilot',
    Cell: ({ row }: Cell) => {
      const order = row.original as IOrder;
      const isSplit = order.splitPartCount != null && order.splitPartCount > 1;
      return (
        <div className="flex flex-col gap-1">
          <div className="flex items-center space-x-2">
            {row.values.pilot && row.values.pilot?.vehicles?.length > 0 && (
              <div className="h-5 w-5 shrink-0 rounded-full bg-primary-50 flex items-center justify-center">
                <img
                  src={VehicleIcon(row.values.pilot?.vehicles[0].type)}
                  alt="vehicle"
                />
              </div>
            )}
            <p className="truncate" title={row.values.pilot?.user.firstName}>
              {row.values.pilot ? row.values.pilot.user.firstName : '-'}
            </p>
          </div>
          {isSplit && (
            <span className="text-[10px] text-sky-700 bg-sky-100 border border-sky-300 rounded-md px-1 py-[1px] w-fit">
              Split · leg {order.splitPartIndex}/{order.splitPartCount}
            </span>
          )}
        </div>
      );
    },
  },
  {
    Header: 'Rider status',
    accessor: 'riderStatus',
    Cell: ({ row }: Cell) => {
      const order = row.original as IOrder;
      const progress = order.pickupProgress;
      return (
        <div className="flex flex-col gap-1 whitespace-nowrap">
          <span>{order.riderStatus || '-'}</span>
          {progress && (
            <span className="text-xs text-neutral-500">
              {progress.total === 0
                ? 'All pickups cancelled'
                : `${progress.collected} of ${progress.total} picked up`}
            </span>
          )}
        </div>
      );
    },
  },
  {
    Header: 'Delivery Address',
    accessor: 'deliveryAddress',
    Cell: ({ row }: Cell) => (
      <span>{row.values.deliveryAddress?.address || '-'}</span>
    ),
  },
  {
    Header: 'Tenant/Kitchen',
    accessor: 'kitchens',
    Cell: ({ row }: Cell) => {
      const order = row.original as IOrder;
      // Check if external order (slug starts with EXT_)
      if (order.slug?.startsWith('EXT_') && order.metadata?.tenantName) {
        return order.metadata.tenantName;
      }
      return row.values.kitchens[0]?.name || '-';
    },
  },
  {
    Header: 'Kitchen Staffs',
    accessor: 'assignedKitchenStaffs',
    Cell: ({ row }: Cell) =>
      row.values.assignedKitchenStaffs
        ?.map(
          (staff: { role: KitchenRole[]; user: IUser }) =>
            `${staff.user.firstName} ${staff.user.lastName}`
        )
        .join(', '),
  },
  {
    Header: 'Order Amount',
    accessor: 'totalPrice',
    Cell: ({ row }: Cell) => `₦${numeral(row.values.totalPrice).format('0,0')}`,
  },
  {
    Header: 'Status',
    accessor: 'status',
  },
  {
    Header: 'Quick Actions',
    accessor: 'user',
  },
];

const orderLabels = [
  { value: 'OFFICE', option: 'Office' },
  { value: 'HOME', option: 'Home' },
  { value: 'OTHER', option: 'Other' },
  { value: 'WORK', option: 'Work' },
] as { value: string; option: string }[];

const orderViews = [{ name: 'List' }, { name: 'Map' }];

const orderQuantity = [
  { name: '1', value: 1 },
  { name: '2', value: 2 },
  { name: '3', value: 3 },
  { name: '4', value: 4 },
  { name: '5', value: 5 },
  { name: '6', value: 6 },
  { name: '7', value: 7 },
];

const orderServiceDetails = {
  DELIVERY: { label: 'Delivery', Icon: TruckIcon },
  PICKUP: { label: 'Pickup', Icon: ShoppingBagIcon },
  SCHEDULED: { label: 'Scheduled', Icon: ClockIcon },
  GROUPS: { label: 'Groups', Icon: UserGroupIcon },
  EVENTS: { label: 'Events', Icon: CalendarDaysIcon },
  BULK: { label: 'Bulk', Icon: CubeIcon },
};

export const returnDeliveryInformation = (order: IOrder) => {
  const isExternal = order.slug?.startsWith('EXT_');
  const pickupStops = getOrderPickupStops(order);
  const riderContact = order.pilot?.user?.phoneNumber?.trim();
  const riderContactIsEmail = riderContact?.includes('@');
  const serviceDetails = order.service
    ? orderServiceDetails[order.service]
    : undefined;
  const ServiceIcon = serviceDetails?.Icon ?? QuestionMarkCircleIcon;

  const baseFields = [
    ...(pickupStops.length
      ? pickupStops.map((stop, index) => ({
          title: `Pickup ${index + 1}`,
          value: (
            <span className="flex flex-col gap-1">
              <span className="medium">
                {stop.name || stop.outletCode || 'Pickup location'}
              </span>
              <span>{stop.address || stop.area || 'Address unavailable'}</span>
              {(stop.assignedPackCount ?? stop.packCount) != null && (
                <span className="text-xs text-neutral-500">
                  {stop.assignedPackCount ?? stop.packCount}{' '}
                  {(stop.assignedPackCount ?? stop.packCount) === 1
                    ? 'pack'
                    : 'packs'}
                </span>
              )}
            </span>
          ),
          changeButton: false,
        }))
      : [
          {
            title: isExternal ? 'Pickup Store' : 'Kitchen Assigned',
            value: isExternal
              ? order.metadata?.pickupStoreName ||
                (order.metadata?.routePlan
                  ? 'Pickup locations unavailable for this rider'
                  : order.pickupAddress?.name || 'Unavailable')
              : order.kitchens?.map((kitchen) => kitchen.name).join(', ') ||
                'Unassigned',
            changeButton: !isExternal,
          },
          ...(isExternal && !order.metadata?.routePlan && order.pickupAddress
            ? [
                {
                  title: 'Pickup Address',
                  value: formatOrderAddress(order.pickupAddress),
                  changeButton: false,
                },
              ]
            : []),
        ]),
    {
      title: 'Delivery Rider Assigned',
      value:
        order?.pilot &&
        order.pilot.user &&
        `${order.pilot.user.firstName} ${order.pilot.user.lastName}`,
      changeButton: true,
    },
    {
      title: riderContactIsEmail
        ? "Delivery Rider's email"
        : "Delivery Rider's phone number",
      value: riderContact || 'Unavailable',
      changeButton: false,
    },
    {
      title: 'Order Channel',
      value:
        order.channel?.channelName ||
        (isExternal ? 'External order' : 'Unavailable'),
      changeButton: false,
    },
    {
      title: 'Assigned kitchen staffs',
      value: order.assignedKitchenStaffs
        ?.map(
          (staff) =>
            `${staff.user.firstName} ${staff.user.lastName}: ${staff.role.join(
              ', '
            )}`
        )
        .join(', '),
      changeButton: false,
    },
    {
      title: 'Service Type',
      value: (
        <span className="inline-flex items-center gap-2">
          <ServiceIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{serviceDetails?.label ?? order.service ?? 'Unavailable'}</span>
        </span>
      ),
      changeButton: false,
    },
  ];

  const externalFields = isExternal
    ? [
        {
          title: 'Tenant Name',
          value: order.metadata?.tenantName,
          changeButton: false,
        },
        {
          title: 'Pickup Contact Person',
          value: pickupStops.length
            ? undefined
            : order.metadata?.pickupContactPerson,
          changeButton: false,
        },
        {
          title: 'Pickup Phone Number',
          value: pickupStops.length
            ? undefined
            : order.metadata?.pickupPhoneNumber,
          changeButton: false,
        },
        {
          title: 'Pickup Email',
          value: pickupStops.length ? undefined : order.metadata?.pickupEmail,
          changeButton: false,
        },
        {
          title: 'Customer Phone (from tenant)',
          value: order.metadata?.customerPhoneNumber,
          changeButton: false,
        },
      ]
    : [];

  return [...baseFields, ...externalFields].filter(
    (field) =>
      field.changeButton ||
      (field.value !== undefined && field.value !== null && field.value !== '')
  );
};

export const initialOrdersStatuses: OrdersStatuses = {
  PLACED: 0,
  PREPARING: 0,
  READY_FOR_PICKUP: 0,
  CONFIRMED: 0,
  ACCEPTED: 0,
  ENROUTE: 0,
  DELIVERED: 0,
  CANCELED: 0,
  SCHEDULED: 0,
};

export const flaggedOrderColumns = [
  {
    Header: 'Order Slug',
    accessor: 'order',
    Cell: ({ row }: Cell) => row.values.order.slug,
  },
  {
    Header: 'Date & Time',
    accessor: 'createdAt',
  },
  {
    Header: 'Pilot',
    accessor: 'pilot',
    Cell: ({ row }: Cell) => (
      <div className="flex items-center space-x-2">
        <p>{row.values.pilot ? row.values.pilot.user.firstName : '-'}</p>
      </div>
    ),
  },
  {
    Header: 'Flag',
    accessor: 'flag',
    Cell: ({ row }: Cell) => row.values.flag.value,
  },
  // {
  //   Header: 'Customer',
  //   accessor: 'customer',
  //   Cell: ({ row }: Cell) =>
  //     row.values.customer
  //       ? row.values.customer.fullName
  //       : row.values.user
  //       ? `${row.values.user.firstName} ${row.values.user.lastName}`
  //       : '-',
  // },
  // {
  //   Header: 'Delivery Address',
  //   accessor: 'deliveryAddress',
  //   Cell: ({ row }: Cell) => row.values.deliveryAddress.address,
  // },
  {
    Header: 'Status',
    accessor: 'status',
  },
];

export {
  paymentMethods,
  orderStatus,
  statusColors,
  orderGroups,
  paymentStatus,
  orderColumns,
  orderLabels,
  orderViews,
  orderQuantity,
};
